import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CalendarPM from '../common/CalendarPM/CalendarPM';
import API from '../../utils/ApiConfig';
import { CREATE_RELEASE_NOTES, LATEST_RELEASE_NOTES } from '../../utils/ApiEndpoints';
import ErrorMessage from '../common/ErrorMessage/ErrorMessage';
import SuccessMessage from '../common/SuccessMessage/SuccessMessage';
import Loader from '../common/Loader/Loader';

export const ReleaseNotes = () => {
  const { t } = useTranslation();
  const tc = (e, data) => (data ? t(`release_notes:${e}`, data) : t(`release_notes:${e}`));
  const [date, setDate] = useState(null);
  const [latestReleaseNotesDate, setLatestReleaseNotesDate] = useState(null);
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [yearRange, setYearRange] = useState('');

  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getLatestReleaseNotes = () => API.get(LATEST_RELEASE_NOTES, { baseURL: '' });
  const addNewReleaseNotes = () =>
    API.post(
      CREATE_RELEASE_NOTES,
      {
        title: title,
        url: url,
        releaseDate: formatDate(date),
      },
      { baseURL: '' }
    );

  useEffect(() => {
    setIsLoading(true);

    const currentYear = new Date().getFullYear();
    setYearRange(currentYear + ':' + (currentYear + 1));
    let timeZoneOffset = new Date().getTimezoneOffset();

    getLatestReleaseNotes()
      .then(response => {
        if (response.data) {
          let responseDate = new Date(response.data.releaseDate);
          let isoDate = moment(responseDate).add(timeZoneOffset, 'minutes').toDate();
          setLatestReleaseNotesDate(isoDate);
          setDate(isoDate);
          setTitle(response.data.title);
          setUrl(response.data.url);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  const isPublishButtonEnabled = useMemo(() => {
    return !!date && !!title && !!url;
  }, [date, title, url]);

  const closeErrorMessage = () => {
    setShowErrorMessage(false);
  };

  const formatDate = timestamp => {
    let m = moment(timestamp);
    return m.format('MM/DD/YYYY');
  };

  const handleSubmit = event => {
    event.preventDefault();
    setIsLoading(true);

    addNewReleaseNotes()
      .then(() => {
        setSuccessMessage('CREATE_RELEASE_NOTES_SUCCESS');
        setShowSuccessMessage(true);
      })
      .catch(() => {
        setErrorMessage('CREATE_RELEASE_NOTES_FAIL');
        setShowErrorMessage(true);
      })
      .finally(() => setIsLoading(false));
  };

  const handleChangeDate = event => {
    setDate(event.target.value);
  };
  const handleTitleChange = event => {
    setTitle(event.target.value);
  };
  const handleUrlChange = event => {
    setUrl(event.target.value);
  };

  return (
    <Fragment>
      <div className="release_notes">
        {isLoading ? <Loader /> : ''}
        <div className="container-fluid">
          <div className="release_notes-header">
            <h1 className="title">{tc('PUBLISH_RELEASE_NOTES')}</h1>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="release_notes-field-wrapper">
              <div className="release_notes-field-title">{tc('TITLE')}</div>
              <div className="release_notes-input-component">
                <input className="form-control" type="text" value={title} onChange={handleTitleChange} />
              </div>
            </div>
            <div className="release_notes-field-wrapper">
              <div className="release_notes-field-title">{tc('URL')}</div>
              <div className="release_notes-input-component">
                <input className="form-control" type="text" value={url} onChange={handleUrlChange} />
              </div>
            </div>
            <div className="release_notes-field-wrapper">
              <div className="release_notes-field-title">{tc('RELEASE_DATE')}</div>
              <div className="release_notes-input-component">
                <CalendarPM
                  className="release_notes-date"
                  minDate={latestReleaseNotesDate}
                  monthNavigator={true}
                  yearNavigator={true}
                  yearRange={yearRange}
                  value={date}
                  onChange={handleChangeDate}
                />
              </div>
            </div>
            <div className="release_notes-publish-button">
              <button className="btn btn-primary" type="submit" disabled={!isPublishButtonEnabled}>
                Publish
              </button>
            </div>
          </form>
        </div>
      </div>

      <ErrorMessage open={showErrorMessage} handleClose={closeErrorMessage} message={tc(errorMessage)} />
      <SuccessMessage open={showSuccessMessage} setOpen={setShowSuccessMessage} message={tc(successMessage)} type="success" />
    </Fragment>
  );
};